import React, { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiChevronLeft,
  FiChevronRight,
  FiShare2,
  FiThumbsDown,
  FiThumbsUp
} = FiIcons;

function HumorCarousel({ jokes, ratings, onRate, onShare, isShared }) {
  const [position, setPosition] = useState(
    () => Math.floor(Math.random() * Math.max(jokes.length, 1))
  );
  const [showHidden, setShowHidden] = useState(false);

  const visibleJokes = useMemo(
    () => jokes.filter((joke) => ratings[joke.title] !== 'down'),
    [jokes, ratings]
  );

  const hiddenJokes = useMemo(
    () => jokes.filter((joke) => ratings[joke.title] === 'down'),
    [jokes, ratings]
  );

  useEffect(() => {
    setPosition((current) => (
      visibleJokes.length ? current % visibleJokes.length : 0
    ));
  }, [visibleJokes.length]);

  useEffect(() => {
    if (visibleJokes.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setPosition((current) => (current + 1) % visibleJokes.length);
    }, 10000);

    return () => window.clearInterval(timer);
  }, [visibleJokes.length]);

  const move = (direction) => {
    if (!visibleJokes.length) return;

    setPosition((current) => {
      const next = current + direction;

      if (next < 0) return visibleJokes.length - 1;
      if (next >= visibleJokes.length) return 0;

      return next;
    });
  };

  const restoreJoke = (title) => {
    onRate(title, null);
    setShowHidden(true);
  };

  return (
    <>
      <div className="humor-carousel" aria-label="Humor-Karussell">
        <button
          className="humor-arrow"
          type="button"
          onClick={() => move(-1)}
          aria-label="Vorherigen Witz anzeigen"
        >
          <SafeIcon icon={FiChevronLeft} aria-hidden="true" />
        </button>

        <div className="humor-viewport">
          <div
            className="humor-track"
            style={{ transform: `translateX(-${position * 100}%)` }}
          >
            {visibleJokes.map((joke) => {
              const rating = ratings[joke.title];
              const shared = isShared(joke);

              return (
                <article
                  className="humor-tile humor-carousel-tile"
                  key={joke.title}
                >
                  <div className="humor-tile-icon" aria-hidden="true">☺</div>
                  <h3>{joke.title}</h3>
                  <p>{joke.text}</p>

                  <div
                    className="humor-rating"
                    aria-label={`Bewertung für ${joke.title}`}
                  >
                    <button
                      className={rating === 'up' ? 'rated' : ''}
                      type="button"
                      onClick={() => onRate(
                        joke.title,
                        rating === 'up' ? null : 'up'
                      )}
                      aria-label="Diesen Witz mag ich"
                      aria-pressed={rating === 'up'}
                    >
                      <SafeIcon icon={FiThumbsUp} aria-hidden="true" />
                    </button>

                    <button
                      className="humor-dislike-button"
                      type="button"
                      onClick={() => onRate(joke.title, 'down')}
                      aria-label="Diesen Witz ausblenden"
                    >
                      <SafeIcon icon={FiThumbsDown} aria-hidden="true" />
                    </button>

                    <button
                      className={`humor-share-button ${shared ? 'shared' : ''}`}
                      type="button"
                      onClick={() => onShare(joke)}
                      aria-label={shared
                        ? `${joke.title} nicht mehr teilen`
                        : `${joke.title} mit anderen teilen`}
                      aria-pressed={shared}
                    >
                      <SafeIcon icon={FiShare2} aria-hidden="true" />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <button
          className="humor-arrow"
          type="button"
          onClick={() => move(1)}
          aria-label="Nächsten Witz anzeigen"
        >
          <SafeIcon icon={FiChevronRight} aria-hidden="true" />
        </button>
      </div>

      <div className="hidden-jokes-menu">
        <button
          className="hidden-jokes-toggle"
          type="button"
          onClick={() => setShowHidden((current) => !current)}
          aria-expanded={showHidden}
        >
          Ausgeblendete Witze
          <span>{hiddenJokes.length}</span>
        </button>

        {showHidden && (
          <div className="hidden-jokes-list">
            {!hiddenJokes.length && (
              <p>Noch keine Witze ausgeblendet.</p>
            )}

            {hiddenJokes.map((joke) => (
              <div className="hidden-joke-item" key={joke.title}>
                <strong>{joke.title}</strong>
                <button
                  type="button"
                  onClick={() => restoreJoke(joke.title)}
                >
                  Wieder anzeigen
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default HumorCarousel;